import { Link } from "react-router-dom";
import { ArrowRight, Phone, Check, ShieldCheck, CreditCard, Info, AlertCircle, FileText } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Reveal from "@/components/shared/Reveal";
import { BRAND } from "@/lib/data";

const INCLUDED = [
  "State and Local Taxes",
  "Airport Concession & Facility Fees",
  "Unlimited Mileage (where applicable)",
  "24/7 Breakdown Assistance",
  "Mandatory Insurance Coverage (for selected destinations)",
];

const EXTRAS = [
  "Fuel charges or prepaid fuel options",
  "One-way rental fees",
  "Cross-border travel permits",
  "Early morning, late-night, or out-of-hours pick-up and return charges",
  "Young or senior driver surcharges (age restrictions vary by supplier)",
  "Optional protection plans, including Collision Damage Waiver (CDW) and Theft Protection",
  "Additional drivers",
  "Child seats, infant seats, booster seats, GPS devices, or other accessories",
  "Toll road, congestion, or parking charges",
  "Vehicle upgrades or premium category requests",
];

const DEPOSIT_FACTORS = ["Vehicle category", "Rental supplier", "Destination", "Insurance or protection package selected"];

const TERMS_LOCATIONS = ["During your search results", "At the checkout stage", "In your booking confirmation and itinerary"];

export default function PricingGuide() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#0B2545] text-white pt-16 md:pt-24 pb-14 md:pb-20" data-testid="pricing-guide-hero">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">Pricing Guide</div>
          <h1 className="font-serif text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl">
            Understanding your rental price.
          </h1>
          <p className="mt-8 text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl">
            We believe in transparent pricing, so you know exactly what you're paying for before
            you travel. Below is a breakdown of what's included in your quoted rental price and
            any additional charges that may apply.
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 bg-[#0B2545] text-white flex items-center justify-center">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <span className="overline">Section 01</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-4">
              What's included in your quote.
            </h2>
            <p className="text-[#5B6472] leading-relaxed max-w-md">
              Unless otherwise stated, your quoted rental price typically includes the following:
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-line border-y border-line" data-testid="included-list">
              {INCLUDED.map((item, i) => (
                <Reveal key={item} delay={i * 0.05}>
                  <li className="flex items-center gap-4 py-4">
                    <Check className="h-5 w-5 text-[#0B2545] shrink-0" strokeWidth={2} />
                    <span className="text-[#0B2545]">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <p className="mt-6 text-sm text-[#5B6472]">
              If mandatory insurance is included, it will be clearly indicated during the booking process and on the search results page.
            </p>
            <div className="mt-6 flex gap-3 border-l-2 border-[#0B2545] pl-4 py-2 bg-[#F5F7FA]">
              <Info className="h-4 w-4 text-[#0B2545] shrink-0 mt-0.5" strokeWidth={1.8} />
              <p className="text-sm text-[#0B2545]">
                Please note: Taxes, government levies, and airport fees are subject to change and may vary based on local regulations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Extras */}
      <section className="py-16 md:py-24 bg-[#F5F7FA] border-y border-line">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 bg-white border border-[#0B2545] text-[#0B2545] flex items-center justify-center">
                <AlertCircle className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <span className="overline">Section 02</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-4">
              Additional charges you may encounter.
            </h2>
            <p className="text-[#5B6472] leading-relaxed max-w-md">
              Certain services and optional extras are not included in the quoted rental price
              and may be payable directly to the rental provider at the time of vehicle collection.
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="extras-list">
              {EXTRAS.map((item, i) => (
                <li key={item} className="flex items-start gap-3 bg-white border border-line p-4">
                  <span className="mt-1 font-mono text-[10px] text-[#5B6472] shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-[#0B2545] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-[#5B6472]">
              Most additional charges are payable locally in the destination's currency at the rental desk.
            </p>
          </div>
        </div>
      </section>

      {/* Deposit */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 bg-[#0B2545] text-white flex items-center justify-center">
                <CreditCard className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <span className="overline">Section 03</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-[#0B2545] leading-tight mb-4">
              Security deposit.
            </h2>
            <p className="text-[#5B6472] leading-relaxed max-w-md">
              Most rental companies require a refundable security deposit, which is typically
              pre-authorised on the primary driver's credit card before the vehicle is released.
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="overline mb-4">Deposit amount varies depending on</div>
            <ul className="grid grid-cols-2 gap-3" data-testid="deposit-list">
              {DEPOSIT_FACTORS.map((f) => (
                <li key={f} className="border border-line p-4">
                  <span className="text-[#0B2545] font-medium">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Before you travel */}
      <section className="py-16 md:py-24 bg-[#0B2545] text-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 bg-white text-[#0B2545] flex items-center justify-center">
                <FileText className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <span className="overline-light">Section 04</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight mb-4">
              Before you travel.
            </h2>
            <p className="text-white/80 leading-relaxed max-w-md">
              To avoid surprises at pick-up, we recommend reviewing the Rental Terms & Conditions
              (also referred to as Rules & Restrictions) available:
            </p>
          </div>
          <div className="md:col-span-7">
            <ul className="divide-y divide-white/15 border-y border-white/15" data-testid="terms-list">
              {TERMS_LOCATIONS.map((t, i) => (
                <li key={t} className="flex items-center gap-4 py-4">
                  <span className="font-mono text-[10px] text-white/60">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-white">{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-white/70">
              These documents provide detailed information regarding deposits, optional extras,
              local policies, and any charges specific to your chosen rental supplier.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-white border-t border-line">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0B2545]">
            Questions about pricing? Call us for a transparent quote.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-primary" data-testid="pricing-call">
              <Phone className="h-4 w-4" strokeWidth={2} /> {BRAND.phone}
            </a>
            <Link to="/contact" className="btn-ghost-light">
              Get a free quote <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
